/**
 * Card shown at the top of an archived (withdrawn) proposal's detail pane when
 * one or more actions already ran before the rest was archived — e.g. an email
 * was sent before the proposal was auto-withdrawn.
 *
 * Self-suppressing: returns null when resolution !== 'withdrawn' or when no
 * actions pass `isActionAlreadyExecuted` (which already guards is_current_version
 * and mock/demo-mode metadata internally).
 *
 * Deliberately mirrors WithdrawalExplanation.tsx's anatomy (DetailPanelCard,
 * small leading icon in an auto grid column, title beside it, body under the
 * title) so the two cards read as a unified visual group when rendered together.
 * The jumbo FeaturedIcon treatment read as foreign next to sibling cards.
 */

import { AlertTriangle } from '@untitledui/icons';
import { useMemo } from 'react';

import type { ProposalDetail } from '@/lib/proposals/core/types';
import { getAlreadyExecutedBadgeText } from '@/lib/proposals/utils/actionWarnings';
import { isActionAlreadyExecuted } from '@/lib/proposals/utils/partialExecution';

import { DetailPanelCard } from './shared/DetailPanelCard';

interface ArchivedPartialExecutionBannerProps {
  proposal: Pick<ProposalDetail, 'resolution' | 'actions'>;
}

export function ArchivedPartialExecutionBanner({
  proposal,
}: ArchivedPartialExecutionBannerProps) {
  const executed = useMemo(
    () => proposal.actions.filter(isActionAlreadyExecuted),
    [proposal]
  );

  if (proposal.resolution !== 'withdrawn') {
    return null;
  }

  if (executed.length === 0) {
    return null;
  }

  let title: string;
  let body: string;

  if (executed.length === 1) {
    const action = executed[0];
    if (action.proposed_action_type_name === 'gmail_send') {
      title = 'The email was sent';
      body = 'This was sent before the rest was archived.';
    } else {
      // Convert badge text ("Already created", "Already deleted", etc.) to a
      // declarative sentence appropriate for a card title.
      const badgeText = getAlreadyExecutedBadgeText(action);
      title = badgeText.charAt(0).toUpperCase() + badgeText.slice(1);
      body = 'This happened before the rest was archived.';
    }
  } else {
    const rawLabels = [
      ...new Set(executed.map(a => getAlreadyExecutedBadgeText(a))),
    ]
      .map(l => l.toLowerCase())
      .join(', ');
    const labels = rawLabels.charAt(0).toUpperCase() + rawLabels.slice(1);
    title = 'Some steps already ran';
    body = `${labels} before the rest was archived.`;
  }

  return (
    <div role='status' className='px-3 pt-3 sm:px-4 sm:pt-4 lg:px-6 lg:pt-6'>
      <div className='mx-auto w-full max-w-[1000px]'>
        <DetailPanelCard>
          <div className='grid grid-cols-[auto_1fr] gap-x-2.5 px-4 py-4'>
            <AlertTriangle
              aria-hidden='true'
              className='size-5 flex-shrink-0 self-center text-fg-tertiary'
              strokeWidth={2}
            />
            <span className='text-sm font-semibold text-[var(--color-text-secondary)]'>
              {title}
            </span>
            <p className='col-start-2 pt-1 text-sm leading-6 text-[var(--color-text-tertiary)]'>
              {body}
            </p>
          </div>
        </DetailPanelCard>
      </div>
    </div>
  );
}
